import { css, nil, NO, UIColor, UIRectangle, UITextView, UIView, YES } from "uicore-ts"
import { ValueOf } from "uicore-ts/compiledScripts/UIObject"
import { RHColor } from "./RHColor"
import { RHBadges } from "./Utils_slop/RHDesignTokens"


export type RHAlertBannerTextPresentation = "standard" | "outcome"

export interface RHAlertBannerConfig {
    text?: string
    alertType?: string
    textPresentation?: RHAlertBannerTextPresentation
    badgeText?: string
    backgroundColor?: UIColor
    borderColor?: UIColor
    textColor?: UIColor
    badgeBackgroundColor?: UIColor
    badgeTextColor?: UIColor
    padding?: number | { top?: number, right?: number, bottom?: number, left?: number }
    pointerPosition?: "top-edge-left" | "top-edge-right" | "bottom-edge-left" | "bottom-edge-right" |
        "left-edge-top" | "left-edge-bottom" | "right-edge-top" | "right-edge-bottom"
    pointerInset?: number
    
    // Font Customization
    fontSize?: string | number
    fontWeight?: string | number
    fontFamily?: string
    fontStyle?: "normal" | "italic" | "oblique"
    textDecoration?: "none" | "underline" | "line-through"
    textAlignment?: ValueOf<typeof UITextView.textAlignment>
}

export class RHAlertBannerView extends UIView {
    
    static type = {
        Warning: "warning",
        Info: "info",
        Error: "error",
        Success: "success",
        Question: "question",
        QuestionLightTealBlue: "questionLightTealBlue",
        QuestionTealBlueIndigo: "questionTealBlueIndigo",
        QuestionSteelSlateNavy: "questionSteelSlateNavy",
        QuestionRedCrimsonMaroon: "questionRedCrimsonMaroon",
        QuestionTealGreen: "questionTealGreen",
        AnsweredQuestion: "answeredQuestion",
        UnansweredQuestion: "unansweredQuestion",
        ProcurerBroadcast: "procurerBroadcast",
        Header: "header",
        Custom: "custom"
    }

    static textPresentation = {
        Standard: "standard" as RHAlertBannerTextPresentation,
        Outcome: "outcome" as RHAlertBannerTextPresentation
    }
    
    // --- Subviews ---
    
    _textLabel = new UITextView().configuredWithObject({
        isSingleLine: NO,
        backgroundColor: UIColor.transparentColor,
        style: {
            fontWeight: "500",
            fontSize: "0.95rem",
            lineHeight: "1.5",
            margin: "0",
            overflow: "visible",
            overflowWrap: "anywhere",
            textOverflow: "clip",
            whiteSpace: "normal",
            wordBreak: "break-word"
        }
    }).addedAsSubviewToView(this)
    
    // Note: The badge container uses flex internally to center its text, which is fine
    // because its own frame is set absolutely by the parent.
    _badgeContainer = new UIView().configuredWithObject({
        style: {
            borderRadius: "4px",
            padding: "2px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        }
    }).addedAsSubviewToView(this)
    
    _badgeLabel = new UITextView().configuredWithObject({
        text: "",
        textAlignment: "center",
        style: {
            fontWeight: "bold",
            fontSize: "12px",
            margin: "0",
            userSelect: "none"
        }
    }).addedAsSubviewToView(this._badgeContainer)
    
    // Pointer Subviews
    _pointerBorder = new UIView().configuredWithObject({
        style: { width: "0px", height: "0px", position: "absolute" }
    }).addedAsSubviewToView(this)
    
    _pointerBg = new UIView().configuredWithObject({
        style: { width: "0px", height: "0px", position: "absolute" }
    }).addedAsSubviewToView(this)
    
    // --- State ---
    
    _alertType: string = RHAlertBannerView.type.Warning
    _customConfig: RHAlertBannerConfig = {}
    _pointerPosition: string = nil
    _pointerInset?: number
    
    // Defaults
    _padding = { top: 12, right: 12, bottom: 12, left: 12 }
    _bgColor: UIColor = UIColor.transparentColor
    _borderColor: UIColor = UIColor.transparentColor
    _textColor: UIColor = UIColor.blackColor
    _badgeBgColor: UIColor = UIColor.transparentColor
    _badgeTextColor: UIColor = UIColor.whiteColor
    
    // Font State
    _fontSize: string = "0.95rem"
    _fontWeight: string = "500"
    _fontFamily: string = ""
    _fontStyle: "normal" | "italic" | "oblique" = "normal"
    _textDecoration: "none" | "underline" | "line-through" = "none"
    _textPresentation = RHAlertBannerView.textPresentation.Standard
    // Standard banners follow the shared left-aligned reading pattern. Outcome
    // banners opt into centered emphasis unless an instance sets an alignment.
    _textAlignment: ValueOf<typeof UITextView.textAlignment> = UITextView.textAlignment.left
    _usesDefaultTextAlignment = YES
    
    // Current Pointer Colors
    _currentPointerBgColor: string = ""
    _currentPointerBorderColor: string = ""
    
    // Whether the glass inset highlight overlay is currently active
    _glassEnabled = NO
    
    static hasInjectedStyles = false
    
    constructor(config: RHAlertBannerConfig = {}) {
        super()
        this.applyGlobalStyles()
        this._configureTextLabelForMultiline()
        this.style.borderRadius = "8px"
        this.configureBannerWithObject(config)
        this.badgeText = ""
    }
    
    private _configureTextLabelForMultiline() {
        this._textLabel.isSingleLine = NO
        this._textLabel.style.overflow = "visible"
        this._textLabel.style.overflowWrap = "anywhere"
        this._textLabel.style.textOverflow = "clip"
        this._textLabel.style.whiteSpace = "normal"
        this._textLabel.style.wordBreak = "break-word"
        this._textLabel.textElementView.style.overflow = "visible"
        this._textLabel.textElementView.style.overflowWrap = "anywhere"
        this._textLabel.textElementView.style.textOverflow = "clip"
        this._textLabel.textElementView.style.whiteSpace = "normal"
        this._textLabel.textElementView.style.wordBreak = "break-word"
        this._textLabel.textElementView.style.lineHeight = "1.5"
        // Re-assert here (the single text-label presentation method, also re-run
        // from updateColors) so an alignment change survives later type updates.
        let textAlignment = this._textAlignment
        if (
            this._usesDefaultTextAlignment === YES &&
            this._textPresentation === RHAlertBannerView.textPresentation.Outcome
        ) {
            textAlignment = UITextView.textAlignment.center
        }
        this._textLabel.textAlignment = textAlignment
    }


    configureBannerWithObject(object: RHAlertBannerConfig) {
        
        let needsUpdate = false

        if (object.alertType !== undefined) {
            this.alertType = object.alertType
        }
        if (object.textPresentation !== undefined) {
            this.textPresentation = object.textPresentation
        }
        
        if (object.text !== undefined) {
            this.text = object.text
        }
        if (object.badgeText !== undefined) {
            this.badgeText = object.badgeText
        }
        
        // Properties that mark the view as customized
        if (object.backgroundColor !== undefined) {
            this._bgColor = object.backgroundColor
            this._customConfig.backgroundColor = object.backgroundColor
            needsUpdate = true
        }
        if (object.borderColor !== undefined) {
            this._borderColor = object.borderColor
            this._customConfig.borderColor = object.borderColor
            needsUpdate = true
        }
        if (object.textColor !== undefined) {
            this._textColor = object.textColor
            this._customConfig.textColor = object.textColor
            needsUpdate = true
        }
        if (object.badgeBackgroundColor !== undefined) {
            this._badgeBgColor = object.badgeBackgroundColor
            this._customConfig.badgeBackgroundColor = object.badgeBackgroundColor
            needsUpdate = true
        }
        if (object.badgeTextColor !== undefined) {
            this._badgeTextColor = object.badgeTextColor
            this._customConfig.badgeTextColor = object.badgeTextColor
            needsUpdate = true
        }
        
        // Font Properties
        if (object.fontSize !== undefined) {
            this._fontSize = typeof object.fontSize === "number" ? object.fontSize + "px" : object.fontSize
            this._customConfig.fontSize = this._fontSize
            needsUpdate = true
        }
        if (object.fontWeight !== undefined) {
            this._fontWeight = typeof object.fontWeight === "number" ? object.fontWeight.toString() : object.fontWeight
            this._customConfig.fontWeight = this._fontWeight
            needsUpdate = true
        }
        if (object.fontFamily !== undefined) {
            this._fontFamily = object.fontFamily
            this._customConfig.fontFamily = this._fontFamily
            needsUpdate = true
        }
        if (object.fontStyle !== undefined) {
            this._fontStyle = object.fontStyle
            this._customConfig.fontStyle = this._fontStyle
            needsUpdate = true
        }
        if (object.textDecoration !== undefined) {
            this._textDecoration = object.textDecoration
            this._customConfig.textDecoration = this._textDecoration
            needsUpdate = true
        }
        if (object.textAlignment !== undefined) {
            this.textAlignment = object.textAlignment
        }
        
        if (object.pointerPosition !== undefined) {
            this._pointerPosition = object.pointerPosition
        }
        if (object.pointerInset !== undefined) {
            this._pointerInset = object.pointerInset
        }
        
        if (object.padding !== undefined) {
            if (typeof object.padding === "number") {
                this._padding = {
                    top: object.padding,
                    right: object.padding,
                    bottom: object.padding,
                    left: object.padding
                }
            }
            else {
                this._padding = { ...this._padding, ...object.padding }
            }
        }
        
        if (Object.keys(this._customConfig).length > 0) {
            this._alertType = RHAlertBannerView.type.Custom
        }
        
        if (needsUpdate) {
            this.updateColors()
        }
        
        this.setNeedsLayout()
        return this
    }
    
    get text(): string {
        return this._textLabel.text
    }
    
    set text(text: string) {
        this._textLabel.text = text
        this.setNeedsLayout()
    }

    get textPresentation(): RHAlertBannerTextPresentation {
        return this._textPresentation
    }

    set textPresentation(textPresentation: RHAlertBannerTextPresentation) {
        this._textPresentation = textPresentation
        this.updateColors()
        this.setNeedsLayout()
    }

    get textAlignment(): ValueOf<typeof UITextView.textAlignment> {
        return this._textAlignment
    }

    set textAlignment(textAlignment: ValueOf<typeof UITextView.textAlignment>) {
        this._usesDefaultTextAlignment = NO
        this._textAlignment = textAlignment
        this._configureTextLabelForMultiline()
        this.setNeedsLayout()
    }
    
    get badgeText(): string {
        return this._badgeLabel.text
    }
    
    set badgeText(text: string) {
        this._badgeLabel.text = text
        this._badgeContainer.hidden = (text === nil || text === "")
        this.setNeedsLayout()
    }
    
    get alertType(): string {
        return this._alertType
    }
    
    set alertType(type: string) {
        this._alertType = type
        if (type !== RHAlertBannerView.type.Custom) {
            this._customConfig = {}
        }
        this.updateColors()
        this.setNeedsLayout()
    }
    
    updateColors() {
        // 1. Get defaults based on Alert Type
        let defaults = {
            bg: UIColor.transparentColor,
            border: UIColor.transparentColor,
            text: UIColor.blackColor,
            badgeBg: UIColor.transparentColor,
            badgeText: UIColor.whiteColor,
            fontSize: "0.95rem",
            fontWeight: "500",
            fontFamily: "",
            fontStyle: "normal",
            textDecoration: "none",
            backgroundGradient: "",
            borderGradient: "",
            pointerBg: UIColor.transparentColor
        }
        
        if (this._alertType === RHAlertBannerView.type.Warning) {
            defaults.bg = RHBadges.alertBannerWarningBubbleBackground
            defaults.backgroundGradient = RHBadges.alertBannerWarningBubbleBackgroundGradient
            defaults.borderGradient = RHBadges.alertBannerWarningBubbleBorderGradient
            defaults.border = RHBadges.alertBannerWarningBubbleBorderColor
            defaults.text = RHBadges.alertBannerWarningBubbleTextColor
            defaults.pointerBg = RHBadges.alertBannerWarningBubblePointerBackground
            defaults.badgeBg = RHColor.warning
            defaults.badgeText = RHColor.dark
        }
        else if (this._alertType === RHAlertBannerView.type.Error) {
            defaults.bg = RHColor.danger.colorWithAlpha(0.1)
            defaults.border = RHColor.danger
            defaults.text = RHColor.danger
            defaults.badgeBg = RHColor.danger
            defaults.badgeText = RHColor.textWhite
        }
        else if (this._alertType === RHAlertBannerView.type.Success) {
            // defaults.bg = RHColor.success.colorWithAlpha(0.1)
            defaults.bg = RHBadges.alertBannerSuccessBubbleBackground
            defaults.backgroundGradient = RHBadges.alertBannerSuccessBubbleBackgroundGradient
            defaults.borderGradient = RHBadges.alertBannerSuccessBubbleBorderGradient
            // defaults.border = RHColor.success
            defaults.border = RHBadges.alertBannerSuccessBubbleBorderColor
            // defaults.text = RHColor.success
            defaults.text = RHBadges.alertBannerSuccessBubbleTextColor
            defaults.pointerBg = RHBadges.alertBannerSuccessBubblePointerBackground
            defaults.badgeBg = RHColor.success
            defaults.badgeText = RHColor.textWhite
        }
        else if (this._alertType === RHAlertBannerView.type.Question ||
            this._alertType === RHAlertBannerView.type.QuestionLightTealBlue) {
            defaults.bg = RHBadges.alertBannerQuestionLightTealBlueBubbleBackground
            defaults.backgroundGradient = RHBadges.alertBannerQuestionLightTealBlueBubbleBackgroundGradient
            defaults.borderGradient = RHBadges.alertBannerQuestionLightTealBlueBubbleBorderGradient
            defaults.border = RHBadges.alertBannerQuestionLightTealBlueBubbleBorderColor
            defaults.text = RHBadges.alertBannerQuestionLightTealBlueBubbleTextColor
            defaults.pointerBg = RHBadges.alertBannerQuestionLightTealBlueBubblePointerBackground
        }
        else if (this._alertType === RHAlertBannerView.type.QuestionTealBlueIndigo) {
            defaults.bg = RHBadges.alertBannerQuestionTealBlueIndigoBubbleBackground
            defaults.backgroundGradient = RHBadges.alertBannerQuestionTealBlueIndigoBubbleBackgroundGradient
            defaults.borderGradient = RHBadges.alertBannerQuestionTealBlueIndigoBubbleBorderGradient
            defaults.border = RHBadges.alertBannerQuestionTealBlueIndigoBubbleBorderColor
            defaults.text = RHBadges.alertBannerQuestionTealBlueIndigoBubbleTextColor
            defaults.pointerBg = RHBadges.alertBannerQuestionTealBlueIndigoBubblePointerBackground
        }
        else if (this._alertType === RHAlertBannerView.type.QuestionSteelSlateNavy) {
            defaults.bg = RHBadges.alertBannerQuestionSteelSlateNavyBubbleBackground
            defaults.backgroundGradient = RHBadges.alertBannerQuestionSteelSlateNavyBubbleBackgroundGradient
            defaults.borderGradient = RHBadges.alertBannerQuestionSteelSlateNavyBubbleBorderGradient
            defaults.border = RHBadges.alertBannerQuestionSteelSlateNavyBubbleBorderColor
            defaults.text = RHBadges.alertBannerQuestionSteelSlateNavyBubbleTextColor
            defaults.pointerBg = RHBadges.alertBannerQuestionSteelSlateNavyBubblePointerBackground
        }
        else if (this._alertType === RHAlertBannerView.type.QuestionRedCrimsonMaroon) {
            defaults.bg = RHBadges.alertBannerQuestionRedCrimsonMaroonBubbleBackground
            defaults.backgroundGradient = RHBadges.alertBannerQuestionRedCrimsonMaroonBubbleBackgroundGradient
            defaults.borderGradient = RHBadges.alertBannerQuestionRedCrimsonMaroonBubbleBorderGradient
            defaults.border = RHBadges.alertBannerQuestionRedCrimsonMaroonBubbleBorderColor
            defaults.text = RHBadges.alertBannerQuestionRedCrimsonMaroonBubbleTextColor
            defaults.pointerBg = RHBadges.alertBannerQuestionRedCrimsonMaroonBubblePointerBackground
        }
        else if (this._alertType === RHAlertBannerView.type.QuestionTealGreen) {
            defaults.bg = RHBadges.alertBannerQuestionTealGreenBubbleBackground
            defaults.backgroundGradient = RHBadges.alertBannerQuestionTealGreenBubbleBackgroundGradient
            defaults.borderGradient = RHBadges.alertBannerQuestionTealGreenBubbleBorderGradient
            defaults.border = RHBadges.alertBannerQuestionTealGreenBubbleBorderColor
            defaults.text = RHBadges.alertBannerQuestionTealGreenBubbleTextColor
            defaults.pointerBg = RHBadges.alertBannerQuestionTealGreenBubblePointerBackground
        }
        else if (this._alertType === RHAlertBannerView.type.AnsweredQuestion) {
            defaults.bg = RHBadges.alertBannerAnsweredQuestionBubbleBackground
            defaults.backgroundGradient = RHBadges.alertBannerAnsweredQuestionBubbleBackgroundGradient
            defaults.borderGradient = RHBadges.alertBannerAnsweredQuestionBubbleBorderGradient
            defaults.border = RHBadges.alertBannerAnsweredQuestionBubbleBorderColor
            defaults.text = RHBadges.alertBannerAnsweredQuestionBubbleTextColor
            defaults.pointerBg = RHBadges.alertBannerAnsweredQuestionBubblePointerBackground
        }
        else if (this._alertType === RHAlertBannerView.type.UnansweredQuestion) {
            defaults.bg = RHBadges.alertBannerUnansweredQuestionBubbleBackground
            defaults.backgroundGradient = RHBadges.alertBannerUnansweredQuestionBubbleBackgroundGradient
            defaults.borderGradient = RHBadges.alertBannerUnansweredQuestionBubbleBorderGradient
            defaults.border = RHBadges.alertBannerUnansweredQuestionBubbleBorderColor
            defaults.text = RHBadges.alertBannerUnansweredQuestionBubbleTextColor
            defaults.pointerBg = RHBadges.alertBannerUnansweredQuestionBubblePointerBackground
        }
        else if (this._alertType === RHAlertBannerView.type.ProcurerBroadcast) {
            defaults.bg = RHBadges.alertBannerProcurerBroadcastBubbleBackground
            defaults.backgroundGradient = RHBadges.alertBannerProcurerBroadcastBubbleBackgroundGradient
            defaults.borderGradient = RHBadges.alertBannerProcurerBroadcastBubbleBorderGradient
            defaults.border = RHBadges.alertBannerProcurerBroadcastBubbleBorderColor
            defaults.text = RHBadges.alertBannerProcurerBroadcastBubbleTextColor
            defaults.pointerBg = RHBadges.alertBannerProcurerBroadcastBubblePointerBackground
        }
        else if (this._alertType === RHAlertBannerView.type.Header) {
            defaults.bg = UIColor.transparentColor
            defaults.border = UIColor.transparentColor
            defaults.text = new UIColor("black")
            defaults.fontWeight = "bold"
        }
        else {
            // Info / Custom fallback
            defaults.bg = RHColor.info.colorWithAlpha(0.1)
            defaults.border = RHColor.info
            defaults.text = RHColor.info
            defaults.badgeBg = RHColor.info
            defaults.badgeText = RHColor.textWhite
        }
        
        // 2. Merge with Custom Config
        const finalBg = this._customConfig.backgroundColor || defaults.bg
        const finalBorder = this._customConfig.borderColor || defaults.border
        const finalText = this._customConfig.textColor || defaults.text
        const finalBadgeBg = this._customConfig.badgeBackgroundColor || defaults.badgeBg
        const finalBadgeText = this._customConfig.badgeTextColor || defaults.badgeText
        const finalBackgroundGradient = defaults.backgroundGradient
        const finalBorderGradient = defaults.borderGradient
        const finalPointerBg = defaults.pointerBg || finalBg
        
        const finalFontSize = this._customConfig.fontSize || defaults.fontSize
        let finalFontWeight = this._customConfig.fontWeight || defaults.fontWeight
        if (this._textPresentation === RHAlertBannerView.textPresentation.Outcome) {
            finalFontWeight = RHBadges.alertBannerOutcomeFontWeight
        }
        const finalFontFamily = this._customConfig.fontFamily || defaults.fontFamily
        const finalFontStyle = this._customConfig.fontStyle || defaults.fontStyle
        const finalTextDecoration = this._customConfig.textDecoration || defaults.textDecoration
        
        // 3. Apply to Views
        this.backgroundColor = finalBg
        if (finalBorderGradient) {
            this.style.background = `linear-gradient(${finalBg.stringValue}, ${finalBg.stringValue}) padding-box, ${finalBorderGradient} border-box`
            this.style.border = `${RHBadges.alertBannerGradientBorderWidth}px solid transparent`
            this.style.borderColor = "transparent"
        }
        else {
            this.style.background = finalBackgroundGradient || finalBg.stringValue
            this.style.border = ""
            this.style.borderColor = finalBorder.stringValue
        }
        this._textLabel.textColor = finalText
        
        this._badgeContainer.backgroundColor = finalBadgeBg
        this._badgeLabel.textColor = finalBadgeText
        
        this._textLabel.style.fontSize = finalFontSize + ""
        this._textLabel.style.fontWeight = finalFontWeight + ""
        this._textLabel.style.fontStyle = finalFontStyle
        this._textLabel.style.textDecoration = finalTextDecoration
        this._configureTextLabelForMultiline()
        
        if (finalFontFamily) {
            this._textLabel.style.fontFamily = finalFontFamily
        }
        else {
            this._textLabel.style.fontFamily = ""
        }
        
        this._currentPointerBgColor = finalPointerBg.stringValue
        this._currentPointerBorderColor = finalBorder.stringValue
        
        const shouldHaveGlass = (
            this._alertType === RHAlertBannerView.type.Question ||
            this._alertType === RHAlertBannerView.type.QuestionLightTealBlue ||
            this._alertType === RHAlertBannerView.type.QuestionTealBlueIndigo ||
            this._alertType === RHAlertBannerView.type.QuestionSteelSlateNavy ||
            this._alertType === RHAlertBannerView.type.QuestionRedCrimsonMaroon ||
            this._alertType === RHAlertBannerView.type.QuestionTealGreen ||
            this._alertType === RHAlertBannerView.type.AnsweredQuestion ||
            this._alertType === RHAlertBannerView.type.UnansweredQuestion ||
            this._alertType === RHAlertBannerView.type.ProcurerBroadcast
        )
        if (shouldHaveGlass) {
            this._applyGlassInset()
        }
        else {
            this._removeGlassInset()
        }
    }
    
    static hasInjectedGlassStyles = false
    
    static _injectGlassStyles(styleClassName: string) {
        if (RHAlertBannerView.hasInjectedGlassStyles) { return }
        RHAlertBannerView.hasInjectedGlassStyles = true
        UIView.injectCSS(css`
            .${styleClassName}--glass::before {
                content: '';
                position: absolute;
                inset: ${RHBadges.alertBannerGlassInset}px;
                border-radius: 4px;
                z-index: 0;
                pointer-events: none;
                background: ${RHBadges.alertBannerGlassOverlayBackground};
                box-shadow: ${RHBadges.alertBannerGlassOverlayBoxShadow};
            }
        `, `RHAlertBannerView_GlassStyles`)
    }
    
    _applyGlassInset() {
        if (this._glassEnabled) { return }
        this._glassEnabled = YES
        RHAlertBannerView._injectGlassStyles(this.styleClassName)
        this.viewHTMLElement.classList.add(`${this.styleClassName}--glass`)
        // The text label and badge must sit above the ::before layer
        this._textLabel.style.zIndex = "1"
        this._badgeContainer.style.zIndex = "1"
    }
    
    _removeGlassInset() {
        if (!this._glassEnabled) { return }
        this._glassEnabled = NO
        this.viewHTMLElement.classList.remove(`${this.styleClassName}--glass`)
        this._textLabel.style.zIndex = ""
        this._badgeContainer.style.zIndex = ""
    }
    
    applyGlobalStyles() {
        if (RHAlertBannerView.hasInjectedStyles || this.class !== RHAlertBannerView) {
            return
        }
        // Position relative is required for the absolute pointer triangles to work correctly
        // Removed display: flex to adhere to framework absolute positioning requirements
        UIView.injectCSS(css`
            .${this.styleClassName} {
                position: relative;
                overflow: visible;
                box-sizing: border-box;
            }
        `, `RHAlertBannerView_GlobalStyles`)
        
        RHAlertBannerView.hasInjectedStyles = true
    }
    
    
    override layoutSubviews() {
        
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const outerBounds = this.bounds
        const bounds = this.contentBoundsWithInsets(
            this._padding.left ?? padding,
            this._padding.right ?? padding,
            this._padding.bottom ?? padding,
            this._padding.top ?? padding
        )
            .rectangleByAddingX(-this.viewHTMLElement.clientLeft)
            .rectangleByAddingY(-this.viewHTMLElement.clientTop)
        const badgeWidth = this._badgeContainer.hidden ? 0 : 40
        
        if (!this._badgeContainer.hidden) {
            const contentRect = bounds
            contentRect.distributeViewsAlongWidth(
                [this._textLabel, this._badgeContainer],
                1,
                this._padding.right * 0.5,
                [nil, badgeWidth]
            )
        }
        else {
            bounds.assignedAsFrameOfView(this._textLabel)
        }
        
        this._badgeContainer.bounds.assignedAsFrameOfView(this._badgeLabel)
        
        if (this._pointerPosition) {
            this._pointerBorder.hidden = NO
            this._pointerBg.hidden = NO
            const triSize = RHBadges.alertBannerPointerSize
            const borderWidth = RHBadges.alertBannerPointerBorderInset
            const pointerEdgeInset = this._pointerInset ?? RHBadges.alertBannerPointerEdgeInset
            const pos = this._pointerPosition
            let x = 0, y = 0
            const bColor = this._currentPointerBorderColor
            const bgColor = this._currentPointerBgColor
            
            const setupTriangle = (
                view: UIView,
                colorStr: string,
                offset: number,
                pU: boolean,
                pR: boolean,
                pD: boolean,
                pL: boolean
            ) => {
                view.style.borderStyle = "solid"
                const size = triSize + offset
                view.style.borderWidth = "0px"
                if (pU) {
                    view.style.borderWidth = `${size}px ${triSize}px 0 ${triSize}px`
                    view.style.borderColor = `transparent transparent ${colorStr} transparent`
                    view.frame = new UIRectangle(x - triSize, -size + offset, triSize * 2, size)
                }
                else if (pD) {
                    view.style.borderWidth = `0 ${triSize}px ${size}px ${triSize}px`
                    view.style.borderColor = `${colorStr} transparent transparent transparent`
                    view.frame = new UIRectangle(x - triSize, outerBounds.height - offset, triSize * 2, size)
                }
                else if (pL) {
                    view.style.borderWidth = `${triSize}px ${size}px ${triSize}px 0`
                    view.style.borderColor = `transparent ${colorStr} transparent transparent`
                    view.frame = new UIRectangle(-size + offset, y - triSize, size, triSize * 2)
                }
                else if (pR) {
                    view.style.borderWidth = `${triSize}px 0 ${triSize}px ${size}px`
                    view.style.borderColor = `transparent transparent transparent ${colorStr}`
                    view.frame = new UIRectangle(outerBounds.width - offset, y - triSize, size, triSize * 2)
                }
            }
            
            if (pos === "top-edge-left") {
                x = triSize + pointerEdgeInset
                setupTriangle(this._pointerBorder, bColor, 0, true, false, false, false)
                setupTriangle(this._pointerBg, bgColor, borderWidth, true, false, false, false)
            }
            else if (pos === "top-edge-right") {
                x = outerBounds.width - triSize - pointerEdgeInset
                setupTriangle(this._pointerBorder, bColor, 0, true, false, false, false)
                setupTriangle(this._pointerBg, bgColor, borderWidth, true, false, false, false)
            }
            else if (pos === "bottom-edge-left") {
                x = triSize + pointerEdgeInset
                setupTriangle(this._pointerBorder, bColor, 0, false, false, true, false)
                setupTriangle(this._pointerBg, bgColor, borderWidth, false, false, true, false)
            }
            else if (pos === "bottom-edge-right") {
                x = outerBounds.width - triSize - pointerEdgeInset
                setupTriangle(this._pointerBorder, bColor, 0, false, false, true, false)
                setupTriangle(this._pointerBg, bgColor, borderWidth, false, false, true, false)
            }
            else if (pos === "left-edge-top") {
                y = triSize + pointerEdgeInset
                setupTriangle(this._pointerBorder, bColor, 0, false, false, false, true)
                setupTriangle(this._pointerBg, bgColor, borderWidth, false, false, false, true)
            }
            else if (pos === "left-edge-bottom") {
                y = outerBounds.height - triSize - pointerEdgeInset
                setupTriangle(this._pointerBorder, bColor, 0, false, false, false, true)
                setupTriangle(this._pointerBg, bgColor, borderWidth, false, false, false, true)
            }
            else if (pos === "right-edge-top") {
                y = triSize + pointerEdgeInset
                setupTriangle(this._pointerBorder, bColor, 0, false, true, false, false)
                setupTriangle(this._pointerBg, bgColor, borderWidth, false, true, false, false)
            }
            else if (pos === "right-edge-bottom") {
                y = outerBounds.height - triSize - pointerEdgeInset
                setupTriangle(this._pointerBorder, bColor, 0, false, true, false, false)
                setupTriangle(this._pointerBg, bgColor, borderWidth, false, true, false, false)
            }
            
        }
        else {
            this._pointerBorder.hidden = YES
            this._pointerBg.hidden = YES
        }
    }
    
    override intrinsicContentHeight(constrainingWidth: number): number {
        const badgeHeight = this._badgeContainer.hidden ? 0 : 24
        const contentWidth = constrainingWidth - (this._padding.left + this._padding.right)
        const availableWidth = contentWidth - (this._badgeContainer.hidden ? 0 : 40)
        const textHeight = this._textLabel.intrinsicContentHeight(Math.max(0, availableWidth))
        return Math.max(textHeight, badgeHeight) + this._padding.top + this._padding.bottom
    }
    
}
